import { createContext, useContext } from 'react';

import type { Bounds, CoordinateTransformer } from '~/types';
import type { Transform } from '../hooks';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type SvgContextValue = {
  /** Reference to the root SVG DOM element (for direct DOM operations like zoom binding) */
  svg: SVGSVGElement | null;
  /** Geographic bounding box (unpadded) — use for spatial queries, not for rendering */
  bounds: Bounds | null;
  /** Bidirectional coordinate transformer (geo ↔ SVG) with Y-axis flip */
  transformer: CoordinateTransformer | null;
  /** Resolved SVG viewBox string (padded, or overridden via prop) */
  viewBox: string | null;
  /** Current zoom/pan transform from d3-zoom */
  transform: Transform;
};

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const IDENTITY_TRANSFORM: Transform = { k: 1, x: 0, y: 0 };

/* -------------------------------------------------------------------------- */
/*                                   Context                                  */
/* -------------------------------------------------------------------------- */

const SvgContext = createContext<SvgContextValue>({
  svg: null,
  bounds: null,
  transformer: null,
  viewBox: null,
  transform: IDENTITY_TRANSFORM,
});

/**
 * Consume the SVG canvas context.
 * Must be used within a `<SvgCanvas>` component.
 *
 * @returns The current SVG canvas context value
 * @throws {Error} If used outside of a SvgCanvas provider
 */
const useSvgContext = (): SvgContextValue => {
  const ctx = useContext(SvgContext);
  if (!ctx) throw new Error('useSvgContext must be used within <SvgCanvas>');

  return ctx;
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export type { SvgContextValue };
export { SvgContext, useSvgContext };
